<template>
  <Dialog
    :model-value="visible"
    title="Set Appointment Type Color"
    dialog-type="primary"
    dialog-size="small"
    hide-header-icon
    :close-on-overlay="!loading"
    @update:model-value="(value) => { if (!value) handleClose() }"
    @close="handleClose"
  >
    <Loading :loading="loading" loading-text="Saving appointment type..." background="none-background">
      <div class="my-4">
        <div class="title-font">Edit Appointment Type</div>
        <div class="note-font">Editing this appointment type will change it for all providers in your practice.</div>

        <div class="mt-6 p-1">
          <Label label="Appointment Type Name">
            <TextInput
              v-model="internalName"
              data-testid="my-calendar.color-picker-dialog.type-name-input"
              placeholder="Enter appointment type name"
              size="medium"
              class="color-name-input"
            />
          </Label>
        </div>


        <div class="mt-6 p-1">
          <div class="subtitle-font">Color Code</div>
          <div class="sketch-picker">
            <SketchPicker
              v-model:modelValue="internalColor"
              data-testid="my-calendar.color-picker-dialog.color-picker"
            />
          </div>
        </div>
      </div>
    </Loading>

    <template #footer>
      <div class="flex justify-between items-center w-full">
        <ResponsiveButton
          data-testid="my-calendar.color-picker-dialog.cancel-btn"
          button_type="text"
          built_in_theme="dark"
          size="medium"
          display_name="Cancel"
          :disabled="loading"
          @click-button="handleClose"
        />
        <ResponsiveButton
          data-testid="my-calendar.color-picker-dialog.save-btn"
          button_type="filled"
          built_in_theme="dark"
          size="medium"
          display_name="Save"
          :disabled="isSaveDisabled || loading"
          @click-button="handleApply"
        />
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import { SketchPicker } from 'vue-color';
import Loading from '@/NewLibrary/Loading/Loading.vue';

const props = defineProps({
  color: {
    type: String,
    default: '#000000'
  },
  name: {
    type: String,
    default: ''
  },
  visible: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false
  },
  mode: {
    type: String,
    default: 'edit'
  }
});

const emit = defineEmits(['submit', 'close']);

const internalColor = ref(props.color);
const internalName = ref(props.name);

watch(() => props.color, (newVal) => {
  if (newVal !== undefined) {
    internalColor.value = newVal;
  }
}, { immediate: true });

watch(() => props.name, (newVal) => {
  internalName.value = newVal;
}, { immediate: true });

const isSaveDisabled = computed(() => {
  const normalizedName = internalName.value?.trim() || '';
  if (!normalizedName) return true;
  if (props.mode === 'create') return false;
  return normalizedName === (props.name?.trim() || '') && internalColor.value === props.color;
});

const handleApply = () => {
  emit('submit', {
    name: internalName.value?.trim(),
    color: internalColor.value
  });
};

const handleClose = () => {
  if (props.loading) return;
  internalColor.value = props.color;
  internalName.value = props.name;
  emit('close');
};
</script>

<style scoped>
.title-font {
  font-weight: 500;
  color: #333;
  font-size: 18px;
  font-style: normal;
  line-height: 24px;
  letter-spacing: 0.15px;
}

.note-font {
  color: #999;
  font-size: 12px;
  font-style: normal;
  line-height: 16px;
  letter-spacing: 0.2px;
  margin-top: 5px;
}

.subtitle-font,
.color-name-input :deep(.label-text) {
  color: #4A4A4A;
  font-size: 14px;
  font-weight: 500;
}
.color-name-input :deep(.el-input) {
  margin-top: 4px;
}
.color-name-input :deep(.el-input__wrapper) {
  height: 42px;
}

/* SketchPicker css */
.sketch-picker :deep(.vc-sketch-picker) {
  box-shadow: none;
  width: 100%;
  padding: 16px;
  box-sizing: border-box;
}

/* Main color canvas */
.sketch-picker :deep(.vc-sketch-picker .saturation) {
  border-radius: 4px;
  height: 160px;
  padding: 0;
}

/* Slider area spacing */
.sketch-picker :deep(.vc-sketch-picker .controls .sliders) {
  padding: 16px 0;
}

/* Current color preview swatch */
.sketch-picker :deep(.vc-sketch-picker .controls .color) {
  width: 36px;
  height: 36px;
  margin: 16px 0 16px 8px;
}

/* Separate hue and alpha sliders a bit more */
.sketch-picker :deep(.vc-sketch-picker .controls .sliders .alpha) {
  margin-top: 16px;
}

/* Keep the editable fields aligned in one row */
.sketch-picker :deep(.vc-sketch-picker .field){
  display: flex;
  gap: 16px;
}

/* Shared text styles for picker labels and inputs */
.sketch-picker :deep(.vc-sketch-picker .field .vc-editable-input .vc-input-label),
.sketch-picker :deep(.vc-sketch-picker .field .vc-editable-input .vc-input-input){
  color: #374151;
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  
}

/* Match the input styling from the Figma spec */
.sketch-picker :deep(.vc-sketch-picker .field .vc-editable-input .vc-input-input) {
  width: 100%;
  text-align: center;
  padding: 4px 6px;
  border-radius: 4px;
  border: 1px solid #E5E7EB;
  box-shadow: 0 1px 2px 0 rgba(31, 41, 55, 0.08);

}

/* Resize saved color swatches */
.sketch-picker :deep(.vc-sketch-picker .presets .preset-color) {
  width: 24px;
  height: 24px;
  margin: 0 32px 10px 0;
}



</style>